import { Button, Flex, Modal } from 'pink-lava-ui';
import { DataList } from '@/components/ui/list/DataList';

import type { Driver, DriverListFilter, DriverPayload } from '@dc-trans/data-layer';
import { driverSchema } from '@dc-trans/data-layer';
import { CheckCircleIcon, PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { FormModal, useFormModal } from '@/components/ui/modal';
import { Tag, Popconfirm } from 'antd';
import type { FormField } from '@/types/FieldSchema.type';
import { useNotification } from '@/context/notificationContext';
import { useUrlParamsContext } from '@/context/urlParamsContext';
import {
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useResetDriverPasswordMutation,
} from '@/features/driver/api/mutations';
import queryClient from '@/libs/queryClient';
import { useDriverList } from '@/features/driver/api/queries';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import type { ApiErrorNotificationMeta } from '@/context/apiErrorNotificationProvider';
import resetPasswordIlu from '@/assets/reset_password.png';
import ChangeLogsButton from '@/components/common/ChangeLogsButton';

const FORM_DRIVER_DEFAULT_VALUE: Partial<DriverPayload & { is_password_changed: boolean }> = {
  name: '',
  username: '',
  phone: '',
  status: 'active',
  ktp_number: '',
  sim_number: '',
  is_password_changed: false,
};

const DriverPage = () => {
  const { isMobile } = useBreakpoint();
  const { resetFilters, filters } = useUrlParamsContext();
  const showNotification = useNotification();
  const { visible, mode, showModal, hideModal } = useFormModal();
  const [selectedDriverResetPassword, setSelectedDriverResetPassword] = useState<Driver>();
  const [selectedDriver, setSelectedDriver] =
    useState<Partial<DriverPayload & { is_password_changed: boolean }>>(FORM_DRIVER_DEFAULT_VALUE);

  // Mutations
  const createMutation = useCreateDriverMutation({
    meta: {
      message: () => {
        return `Gagal membuat data driver.`;
      },
    } satisfies ApiErrorNotificationMeta,
  });
  const updateMutation = useUpdateDriverMutation({
    meta: {
      message: () => {
        return `Gagal mengubah data pengemudi.`;
      },
    } satisfies ApiErrorNotificationMeta,
  });
  const resetPasswordMutation = useResetDriverPasswordMutation({
    meta: {
      message: () => {
        return `Gagal mereset password pengemudi.`;
      },
    } satisfies ApiErrorNotificationMeta,
  });

  // Form fields configuration
  const driverFields: FormField<DriverPayload>[] = [
    {
      name: 'name',
      type: 'text',
      label: 'Nama Pengemudi',
      placeholder: 'Masukkan nama pengemudi',
      autoComplete: 'off',
      required: true,
    },
    {
      name: 'phone',
      type: 'text',
      prefix: <div>+62</div>,
      label: 'Nomor Handphone',
      placeholder: 'Masukkan nomor handphone',
      autoComplete: 'off',
      required: true,
    },
    {
      name: 'sim_number',
      type: 'text',
      label: 'Nomor SIM',
      placeholder: 'Masukkan nomor SIM',
      autoComplete: 'off',
      required: true,
    },
    {
      name: 'ktp_number',
      type: 'text',
      label: 'Nomor KTP',
      autoComplete: 'off',
      placeholder: 'Masukkan nomor KTP',
      required: true,
    },
    {
      name: 'username',
      type: 'text',
      label: 'Username',
      autoComplete: 'off',
      placeholder: 'Masukkan username',
      info: `Masukkan 6-15 karakter.\nDapat mengandung huruf / angka / garis bawah ( _ ) / titik ( . )`,
      required: true,
    },
    {
      name: 'status',
      type: 'select',
      label: 'Status',
      placeholder: 'Pilih status',
      allowClear: false,
      required: true,
      options: [
        { label: 'Aktif', value: 'active' },
        { label: 'Tidak Aktif', value: 'inactive' },
      ],
    },
  ];

  // Handle form submission
  const handleSubmit = async (formValues: DriverPayload, formMode: 'create' | 'update') => {
    const payload = {
      name: formValues.name!,
      username: formValues.username!,
      phone: `62${formValues.phone!}`,
      status: formValues.status!,
      ktp_number: formValues.ktp_number!,
      sim_number: formValues.sim_number!,
    };

    if (formMode === 'create') {
      await createMutation.mutateAsync(payload);
      showNotification.success({
        message: `Berhasil membuat data driver baru.`,
      });
      queryClient.invalidateQueries({ queryKey: ['driver'] });
    } else {
      await updateMutation.mutateAsync({ ...payload, ulid: selectedDriver.ulid });
      showNotification.success({
        message: `Berhasil mengubah data driver.`,
      });
      queryClient.invalidateQueries({ queryKey: ['driver'] });
    }
    resetFilters();
    hideModal();
  };

  // Handle create action
  const handleCreate = () => {
    setSelectedDriver(FORM_DRIVER_DEFAULT_VALUE);
    showModal('create');
  };

  // Handle update action
  const handleUpdate = (driver: Driver) => {
    const initialValues: Driver = {
      ulid: driver.ulid,
      name: driver.name,
      username: driver.username,
      phone: driver.phone?.includes('62') ? driver.phone.slice(2) : driver.phone,
      status: driver.status,
      ktp_number: driver.ktp_number || '',
      sim_number: driver.sim_number || '',
      is_password_changed: driver.is_password_changed,
    };
    setSelectedDriver(initialValues);
    showModal('update');
  };

  // Handle reset password action
  const handleResetPassword = async (driver: Driver) => {
    if (!driver.ulid) {
      showNotification.error({
        message: `Gagal mereset password. Pengemudi tidak memiliki ulid.`,
      });
      return;
    }
    await resetPasswordMutation.mutateAsync(driver.ulid);
    setSelectedDriverResetPassword(driver);

    queryClient.invalidateQueries({ queryKey: ['driver'] });
  };

  return (
    <DataList
      title="Pengemudi"
      filterFields={[
        {
          type: 'search',
          name: 'search',
          label: 'Cari Pengemudi',
          placeholder: 'Cari nama / username / no. hp pengemudi',
          trigger: 'onChange',
        },
        {
          type: 'select',
          name: 'status',
          placeholder: 'Semua Status',
          label: 'Status',
          rounded: 'large',
          options: [
            { label: 'Aktif', value: 'active' },
            { label: 'Tidak Aktif', value: 'inactive' },
          ],
        },
      ]}
    >
      <Flex gap={16} className="w-full h-full" vertical>
        <DataList.Header<DriverListFilter>
          actions={{
            primary: {
              label: 'Pengemudi',
              onClick: handleCreate,
              icon: <PlusIcon size={isMobile ? 24 : 20} />,
            },
            secondary: <ChangeLogsButton url={'/users/activities'} queryKey={['driver', 'changelogs']} />,
            downloadUpload: {
              excelTemplate: {
                name: 'PENGEMUDI',
                headers: [
                  {
                    header: 'Nama Pengemudi',
                    key: 'name',
                    example: 'John Doe',
                    required: true,
                    width: 20,
                  },
                  {
                    header: 'Nomor Handphone',
                    key: 'phone',
                    example: '6281234567890',
                    required: true,
                    width: 20,
                  },
                  {
                    header: 'Nomor SIM',
                    key: 'sim_number',
                    example: '3171051234560001',
                    required: true,
                    width: 20,
                  },
                  {
                    header: 'Nomor KTP',
                    key: 'ktp_number',
                    example: '3171051234560001',
                    required: true,
                    width: 20,
                  },
                  {
                    header: 'Username',
                    key: 'username',
                    example: 'johndoe',
                    required: true,
                    width: 20,
                  },
                  {
                    header: 'Status',
                    key: 'status',
                    reference: 'Status',
                    example: 'Aktif',
                    required: true,
                    width: 20,
                  },
                ],
                referenceData: {
                  ['Status']: ['Aktif', 'Tidak Aktif'],
                },
              },
              upload: {
                url: '/drivers/import',
                queryKey: 'driver',
              },
              download: {
                url: '/drivers/export',
                params: filters,
              },
              resourceName: 'pengemudi',
            },
          }}
        />
        <DataList.List<Driver>
          tableSize={isMobile ? 'small' : undefined}
          useQuery={useDriverList}
          emptyDesc="Coba gunakan nama pengemudi atau status lainnya."
          columns={[
            {
              title: 'Nama Pengemudi',
              dataIndex: 'name',
              key: 'name',
              fixed: !isMobile ? 'left' : undefined,
            },
            {
              title: 'Nomor Handphone',
              dataIndex: 'phone',
              key: 'phone',
            },
            {
              title: 'Nomor SIM',
              dataIndex: 'sim_number',
              key: 'sim_number',
            },
            {
              title: 'Nomor KTP',
              dataIndex: 'ktp_number',
              key: 'ktp_number',
            },
            {
              title: 'Username',
              dataIndex: 'username',
              key: 'username',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              align: 'center',
              render: (_, record) => {
                return (
                  <Tag
                    className={`rounded-full! mr-0! ${record.status === 'active' ? 'text-green-700! bg-green-100!' : 'text-red-700! bg-red-100!'}`}
                    bordered={false}
                  >
                    {record.status === 'active' ? 'Aktif' : 'Tidak Aktif'}
                  </Tag>
                );
              },
            },
            {
              title: 'Aksi',
              fixed: !isMobile ? 'right' : undefined,
              align: 'center',
              width: 240,
              render: (_, record) => {
                return (
                  <Flex gap={8} justify="center">
                    <Button type="tertiary" size="small" shape="round" onClick={() => handleUpdate(record)}>
                      Update Data
                    </Button>
                    <Popconfirm
                      title="Konfirmasi"
                      okButtonProps={{
                        size: 'middle',
                        shape: 'round',
                        loading: resetPasswordMutation.isPending,
                      }}
                      cancelButtonProps={{
                        size: 'middle',
                        shape: 'round',
                      }}
                      description={
                        <div>
                          Apakah anda yakin untuk reset password dengan username <strong>{record.username}</strong> ?
                        </div>
                      }
                      onConfirm={() => handleResetPassword(record)}
                      okText="Lanjutkan"
                      disabled={!record.is_password_changed || record.status === 'inactive'}
                      cancelText="Batalkan"
                    >
                      <Button
                        type="secondary"
                        size="small"
                        shape="round"
                        disabled={!record.is_password_changed || record.status === 'inactive'}
                      >
                        Reset Password
                      </Button>
                    </Popconfirm>
                  </Flex>
                );
              },
            },
          ]}
        />
        {/* Form Modal for Create/Update */}
        <FormModal<DriverPayload>
          visible={visible}
          validationMode="onSubmit"
          mode={mode}
          title={mode === 'create' ? 'Tambah Data Pengemudi' : 'Update Data Pengemudi'}
          fields={driverFields}
          initialValues={selectedDriver}
          validationSchema={driverSchema}
          onSubmit={handleSubmit}
          onCancel={hideModal}
          loading={createMutation.isPending || updateMutation.isPending}
        />
        <Modal
          title={''}
          open={!!selectedDriverResetPassword}
          centered
          width={320}
          onCancel={() => setSelectedDriverResetPassword(undefined)}
          footer={
            <Button shape="round" block onClick={() => setSelectedDriverResetPassword(undefined)}>
              OK, Mengerti
            </Button>
          }
        >
          <div className="w-full flex flex-col items-center mt-8 gap-2">
            <img src={resetPasswordIlu} alt="reset_password_ilu" height={160} width={160} />
            <div className="flex flex-row items-center gap-2">
              <CheckCircleIcon size={20} className="text-green-500" />
              <span className="text-lg font-bold text-green-500">Reset Password Berhasil</span>
            </div>
            <div className="font-bold">Username : {selectedDriverResetPassword?.username}</div>
            <div className="text-center text-gray-700">
              Silahkan mengarahkan driver untuk melakukan proses login ulang.
            </div>
          </div>
        </Modal>
      </Flex>
    </DataList>
  );
};

export default DriverPage;
